import type { ContentContainer } from "../core/types";
import type { CalculatorData } from "../modal/types";
import type { ITrackingService } from "../tracking/ITrackingService";
import { Logger } from "../core/Logger";


type ContainerRegistration = {
    container: ContentContainer;
    onSwapToScreening?: ((trackerId: string, calculatorData?: CalculatorData) => void) | undefined;
};

export class PostMessageHandler {
    private static isAttatched = false;
    private static containerMapping: Record<string, ContainerRegistration> = {};
    constructor(
        private tracker: ITrackingService,
        private allowedOrigins: string[] = ['selectfi.com', 'selectfi.app', 'localhost', 'lovable.app'],
    ) {}

    private isValidMessage(message: MessageEvent): boolean {
        try {
            if (message.data.messageOwner !== 'selectfi') {
                return false;
            }
            const url = new URL(message.origin);
            return this.allowedOrigins.some(allowedOrigin => url.hostname.endsWith(allowedOrigin));
        } catch {
            return false;
        }
    }

    public static registerContainer(
        container: ContentContainer,
        onSwapToScreening?: (trackerId: string, calculatorData?: CalculatorData) => void
    ): string {
        const existingEntry = Object.entries(this.containerMapping).find(
            ([_, reg]) => reg.container === container
        );
        if (existingEntry) {
            throw new Error('Container is already registered');
        }
        const id = crypto.randomUUID();
        PostMessageHandler.containerMapping[id] = {
            container,
            onSwapToScreening,
        };
        return id;
    }

    public attatchListener(): void {
        if (PostMessageHandler.isAttatched) {
            Logger.warn('PostMessageHandler listener is already attached.');
            return;
        }
        PostMessageHandler.isAttatched = true;
        window.addEventListener('message', (event: MessageEvent) => {
            if (this.isValidMessage(event)) {
                this.handleMessage(event);
            }
        });
        Logger.debug('PostMessageHandler listener attached.');
    }

    private handleMessage(event: MessageEvent): void {
        Logger.debug('PostMessageHandler received message:', event.data);
        const data: PostMessageData = event.data;
        const registration = PostMessageHandler.containerMapping[data.trackerId];
        if (!registration) {
            Logger.warn('No registered container found for trackerId:', data.trackerId);
            return;
        }

        if (data.event === 'field_interaction') {
            this.tracker.trackFieldInteraction(registration.container, data.fieldName!, data.fieldValue);
        } else if (data.event === 'submit') {
            this.tracker.trackSubmit(registration.container);
        } else if (data.event === 'swap_to_screening') {
            if (registration.onSwapToScreening) {
                Logger.debug('Calling onSwapToScreening callback for trackerId:', data.trackerId);
                registration.onSwapToScreening(data.trackerId, data.calculatorData);
                this.tracker.trackContinueToCreditApp(registration.container);
            } else {
                Logger.warn('Received swap_to_screening event but no callback registered for trackerId:', data.trackerId);
            }
        } else {
            Logger.warn('Unknown event type received in post message:', data.event);
        }
    }
}

type PostMessageData = {
    trackerId: string;
    event: 'field_interaction' | 'submit' | 'swap_to_screening';
    fieldName?: string;
    fieldValue?: string;
    quoteId?: string;
    // Present only on swap_to_screening, and only from a calculator build that sends it.
    calculatorData?: CalculatorData;
};
